import { m } from "~/i18n/messages";
import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { Separator } from "~/components/ui/separator";
import { kanjiItemQueryOptions, useKanjiItem } from "~/lib/client/learn";
import { useSetAtom } from "jotai";
import {
	currentPageContextAtom,
	type ChatWidgetContext,
} from "~/components/practice/chat-widget";
import { generateLearnKanjiItemPageContext } from "~/lib/context-gen";
import { SuspenseFallback } from "~/components/ui/suspense-fallback";
import { Skeleton } from "~/components/ui/skeleton";
import type {
	Word,
	KanjiContent,
} from "~/lib/server/db/schema/flashcard-schema";

function KanjiItemSkeleton() {
	return (
		<div className="max-w-screen-md mx-auto p-4">
			<div className="lg:col-span-2">
				<Skeleton className="h-6 w-32 mb-3" /> {/* Back button */}
			</div>
			<div className="space-y-3 sm:space-y-4">
				{/* Header Section */}
				<div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
					<div className="flex items-center justify-between">
						<div>
							<Skeleton className="h-12 w-16 mb-2" /> {/* Kanji character */}
							<div className="space-y-1">
								<Skeleton className="h-4 w-32" /> {/* Onyomi */}
								<Skeleton className="h-4 w-28" /> {/* Kunyomi */}
							</div>
						</div>
						<Skeleton className="h-6 w-12 rounded-full" /> {/* Level badge */}
					</div>
				</div>

				<div className="space-y-3 sm:space-y-4">
					{/* Meanings Section */}
					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
						<Skeleton className="h-6 w-24 mb-2" /> {/* Meanings title */}
						<div className="space-y-2">
							<Skeleton className="h-4 w-32" />
							<Skeleton className="h-4 w-28" />
							<Skeleton className="h-4 w-36" />
						</div>
					</div>

					{/* Details Section */}
					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
						<Skeleton className="h-6 w-20 mb-2" /> {/* Details title */}
						<Skeleton className="h-4 w-24" /> {/* Strokes */}
					</div>

					{/* Related Words Section */}
					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
						<Skeleton className="h-6 w-32 mb-2" /> {/* Related words title */}
						<div className="space-y-2">
							{Array.from({ length: 3 }).map((_, index) => (
								<div
									// biome-ignore lint/suspicious/noArrayIndexKey: skeleton component
									key={index}
									className="flex items-center gap-2"
								>
									<Skeleton className="h-4 w-16" /> {/* Kanji form */}
									<Skeleton className="h-4 w-20" /> {/* Reading */}
									<Skeleton className="h-4 w-24" /> {/* Translation */}
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/app/learn/jlpt/$level/kanji/$kanjiId")({
	component: KanjiItemComponent,
	loader: async ({ context, params }) => {
		await context.queryClient.ensureQueryData(
			kanjiItemQueryOptions(params.kanjiId),
		);
	},
});

function KanjiItemComponent() {
	const { kanjiId, level } = Route.useParams();

	const allowedLevels = ["n5", "n4", "n3", "n2", "n1"] as const;
	type Level = (typeof allowedLevels)[number];
	const isValidLevel = allowedLevels.includes(level as Level);

	if (!isValidLevel) {
		return <div className="p-8 text-red-500">Invalid JLPT level</div>;
	}

	return (
		<SuspenseFallback
			loading={<KanjiItemSkeleton />}
			error={
				<div className="p-8 text-red-500">
					An error occurred while loading kanji details
				</div>
			}
		>
			<KanjiItemContent kanjiId={kanjiId} level={level} />
		</SuspenseFallback>
	);
}

function KanjiItemContent({ kanjiId, level }: { kanjiId: string; level: string }) {
	const setPageContext = useSetAtom(currentPageContextAtom);
	const { data: kanji } = useKanjiItem(kanjiId);

	const content = kanji?.content as KanjiContent;

	useEffect(() => {
		if (kanji) {
			setPageContext({
				title: kanji.reading,
				slug: `/app/learn/jlpt/${level}/kanji/${kanjiId}`,
				contextData: generateLearnKanjiItemPageContext({
					kanjiItem: {
						id: kanjiId,
						level: kanji.level,
						reading: kanji.reading,
						content: kanji.content as KanjiContent,
					},
				}),
			});
		}
	}, [kanji, kanjiId, setPageContext, level]);

	return (
		<div className="max-w-screen-md mx-auto p-4">
			<div className="lg:col-span-2">
				<Link
					to="/app/learn/jlpt/$level"
					params={{ level }}
					className="flex items-center gap-1 text-pink-500/80 hover:text-pink-600 transition-colors text-sm py-3"
				>
					<ChevronLeft className="w-4 h-4" />
					<span>{m["ui.back_to_list_button"]()}</span>
				</Link>
			</div>
			<div className="space-y-3 sm:space-y-4">
				{/* Header Section */}
				<div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
					<div className="flex items-center justify-between">
						<div>
							<h1 className="text-3xl sm:text-4xl font-bold text-pink-800">
								{kanji.reading}
							</h1>
							<div className="text-pink-600/80 mt-2 text-sm sm:text-base flex flex-col gap-1">
								<span>
									<span className="font-semibold">{m["details_view.onyomi_label"]()}</span>{" "}
									{content.onyomi?.join(", ") ?? "-"}
								</span>
								<span>
									<span className="font-semibold">{m["details_view.kunyomi_label"]()}</span>{" "}
									{content.kunyomi?.join(", ") ?? "-"}
								</span>
							</div>
						</div>
						<span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-xs font-medium">
							{kanji?.level?.toUpperCase()}
						</span>
					</div>
				</div>

				<div className="space-y-3 sm:space-y-4">
					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
						<h2 className="text-lg sm:text-xl font-semibold text-pink-700 mb-2">
							{m["ui.meanings_label"]()}
						</h2>
						<ul className="space-y-2">
							{(content?.meanings || []).map((meaning: string) => (
								<li
									key={meaning}
									className="text-pink-800 text-sm sm:text-base"
								>
									• {meaning}
								</li>
							))}
						</ul>
					</div>

					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
						<h2 className="text-lg sm:text-xl font-semibold text-pink-700 mb-2">
							Details
						</h2>
						<ul className="text-pink-800 text-sm sm:text-base space-y-1">
							<li>
								<span className="font-semibold">{m["details_view.strokes_label"]()}</span>{" "}
								{content?.strokes}
							</li>
						</ul>
					</div>

					{content?.words && content.words.length > 0 && (
						<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
							<h2 className="text-lg sm:text-xl font-semibold text-pink-700 mb-2">
								{m["details_view.related_words_title"]()}
							</h2>
							<ul className="space-y-2">
								{(content?.words || []).map((word: Word) => (
									<li
										key={word.kanjiForms?.join(",") || ""}
										className="text-pink-800 text-sm sm:text-base"
									>
										<span className="font-semibold">
											{word.kanjiForms?.join(", ")}
										</span>
										{word.readings && word.readings.length > 0 && (
											<span className="ml-2 text-pink-600/80">
												[{word.readings.join(", ")}]
											</span>
										)}
										{word.translations?.length > 0 && (
											<span className="ml-2 text-pink-600/80">
												– {word.translations}
											</span>
										)}
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
